import React, { useEffect, useMemo, useRef, useState } from 'react';

type Props = {
  vastXml: string;
  kind: 'video' | 'audio';
  style?: React.CSSProperties;
};

type TrackingMap = Record<string, string[]>;

function text(el: Element | null): string {
  if (!el) return '';
  return (el.textContent || '').trim();
}

function collectAll(elList: NodeListOf<Element>): string[] {
  const res: string[] = [];
  elList.forEach((el) => {
    const t = text(el);
    if (t) res.push(t);
  });
  return res;
}

function parseTimeToSeconds(v: string): number | null {
  if (!v) return null;
  const s = v.trim();
  if (/^\d+%$/.test(s)) return -parseInt(s, 10);
  const m = s.match(/^(\d{1,2}):([0-5]\d):([0-5]\d)(\.\d+)?$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const sec = parseInt(m[3], 10);
  const frac = m[4] ? parseFloat(m[4]) : 0;
  return h * 3600 + min * 60 + sec + frac;
}

function pickMediaFile(doc: Document, kind: 'video' | 'audio') {
  const mediaFiles = Array.from(doc.querySelectorAll('MediaFile'));
  const prefer = kind === 'video'
    ? ['video/mp4', 'video/webm', 'video/quicktime']
    : ['audio/mpeg', 'audio/mp3', 'audio/aac', 'audio/ogg', 'audio/wav'];

  // Progressive preferred
  const sorted = mediaFiles.sort((a, b) => {
    const da = (a.getAttribute('delivery') || '').toLowerCase();
    const db = (b.getAttribute('delivery') || '').toLowerCase();
    const pa = da === 'progressive' ? 0 : 1;
    const pb = db === 'progressive' ? 0 : 1;
    return pa - pb;
  });

  for (const el of sorted) {
    const type = (el.getAttribute('type') || '').toLowerCase();
    if (prefer.includes(type)) {
      const url = text(el);
      if (url) return { url, type };
    }
  }
  // Fallback: first any
  if (sorted.length) {
    const el = sorted[0];
    const url = text(el);
    const type = (el.getAttribute('type') || '').toLowerCase();
    if (url) return { url, type };
  }
  return null;
}

function buildMeta(doc: Document) {
  const impressions = collectAll(doc.querySelectorAll('Impression'));

  const tracking: TrackingMap = {};
  doc.querySelectorAll('Tracking').forEach((t) => {
    const evt = (t.getAttribute('event') || '').toLowerCase();
    const url = text(t);
    if (!evt || !url) return;
    if (!tracking[evt]) tracking[evt] = [];
    tracking[evt].push(url);
  });

  // ClickThrough and ClickTracking
  let clickThrough: string | undefined;
  const clickThroughEl = doc.querySelector('Linear > VideoClicks > ClickThrough, Linear > AudioClicks > ClickThrough, VideoClicks > ClickThrough, AudioClicks > ClickThrough');
  if (clickThroughEl) clickThrough = text(clickThroughEl);
  const clickTrackers = collectAll(doc.querySelectorAll('Linear > VideoClicks > ClickTracking, Linear > AudioClicks > ClickTracking, VideoClicks > ClickTracking, AudioClicks > ClickTracking'));

  // Error URLs
  const errorUrls = collectAll(doc.querySelectorAll('Error'));

  // Skipoffset
  let skipOffsetSec: number | null = null;
  let skipOffsetPercent: number | null = null;
  const linearEl = doc.querySelector('Creative > Linear, Linear');
  const soAttr = linearEl?.getAttribute('skipoffset') || '';
  const so = parseTimeToSeconds(soAttr);
  if (so !== null) {
    if (so < 0) skipOffsetPercent = Math.abs(so);
    else skipOffsetSec = so;
  }

  return { impressions, tracking, clickThrough, clickTrackers, errorUrls, skipOffsetSec, skipOffsetPercent };
}

function ping(urls: string[] | undefined) {
  if (!urls || !urls.length) return;
  urls.forEach((u) => {
    try {
      const sep = u.includes('?') ? '&' : '?';
      const url = u + sep + 'cb=' + Math.floor(Math.random() * 1e9);
      const img = new Image();
      img.src = url;
    } catch {}
  });
}

export default function VastPlayer({ vastXml, kind, style }: Props) {
  const mediaRef = useRef<HTMLVideoElement & HTMLAudioElement>(null as any);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [dur, setDur] = useState<number>(0);
  const [showSkip, setShowSkip] = useState<boolean>(false);
  const [skipShown, setSkipShown] = useState<boolean>(false);
  const [fired, setFired] = useState<{ start?: boolean; q1?: boolean; q2?: boolean; q3?: boolean; complete?: boolean; skip?: boolean }>({});

  const parsed = useMemo(() => {
    if (!vastXml) return null;
    try {
      const doc = new DOMParser().parseFromString(vastXml, 'text/xml');
      const media = pickMediaFile(doc, kind);
      const meta = buildMeta(doc);
      return { media, meta };
    } catch {
      return null;
    }
  }, [vastXml, kind]);

  useEffect(() => {
    setFired({});
    setDur(0);
    setShowSkip(false);
    setSkipShown(false);
  }, [vastXml, kind]);

  useEffect(() => {
    const el = mediaRef.current;
    if (!el || !parsed?.media) return;

    const onLoaded = () => {
      if (isFinite(el.duration) && el.duration > 0) {
        setDur(el.duration);
      }
    };

    const onPlay = () => {
      if (!fired.start) {
        ping(parsed.meta.impressions);
        ping(parsed.meta.tracking['start']);
        setFired((prev) => ({ ...prev, start: true }));
      }
    };

    const onTime = () => {
      const d = dur || el.duration || 0;
      if (!d || !isFinite(d) || d <= 0) return;

      if (!skipShown) {
        const { skipOffsetSec, skipOffsetPercent } = parsed.meta;
        let threshold = Number.POSITIVE_INFINITY;
        if (skipOffsetSec != null) threshold = skipOffsetSec;
        else if (skipOffsetPercent != null) threshold = (skipOffsetPercent / 100) * d;

        if (isFinite(threshold) && threshold >= 0 && el.currentTime >= threshold) {
          setShowSkip(true);
          setSkipShown(true);
        }
      }

      const t = el.currentTime || 0;
      if (!fired.q1 && t >= d * 0.25) {
        ping(parsed.meta.tracking['firstquartile']);
        setFired((p) => ({ ...p, q1: true }));
      }
      if (!fired.q2 && t >= d * 0.5) {
        ping(parsed.meta.tracking['midpoint']);
        setFired((p) => ({ ...p, q2: true }));
      }
      if (!fired.q3 && t >= d * 0.75) {
        ping(parsed.meta.tracking['thirdquartile']);
        setFired((p) => ({ ...p, q3: true }));
      }
    };

    const onEnded = () => {
      if (!fired.complete) {
        ping(parsed.meta.tracking['complete']);
        setFired((p) => ({ ...p, complete: true }));
      }
    };

    const onPause = () => ping(parsed.meta.tracking['pause']);
    const onResume = () => ping(parsed.meta.tracking['resume']);
    const onMuteChange = () => {
      ping(el.muted ? parsed.meta.tracking['mute'] : parsed.meta.tracking['unmute']);
    };
    const onVolumeChange = () => {
      if (el.muted || el.volume === 0) ping(parsed.meta.tracking['mute']);
    };

    const onError = () => {
      ping(parsed.meta.tracking['error']);
      ping(parsed.meta.errorUrls);
    };

    el.addEventListener('loadedmetadata', onLoaded);
    el.addEventListener('play', onPlay);
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('ended', onEnded);
    el.addEventListener('pause', onPause);
    el.addEventListener('playing', onResume);
    el.addEventListener('volumechange', onMuteChange);
    el.addEventListener('volumechange', onVolumeChange);
    el.addEventListener('error', onError);

    return () => {
      el.removeEventListener('loadedmetadata', onLoaded);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('playing', onResume);
      el.removeEventListener('volumechange', onMuteChange);
      el.removeEventListener('volumechange', onVolumeChange);
      el.removeEventListener('error', onError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed, dur, fired.start, fired.q1, fired.q2, fired.q3, fired.complete, skipShown]);

  const onClickOverlay = () => {
    if (!parsed?.meta.clickThrough) return;
    try {
      ping(parsed.meta.clickTrackers);
      window.open(parsed.meta.clickThrough, '_blank', 'noopener,noreferrer');
    } catch {}
  };

  const onSkip = () => {
    if (!parsed) return;
    try {
      if (!fired.skip) {
        ping(parsed.meta.tracking['skip']);
        setFired((p) => ({ ...p, skip: true }));
      }
      if (mediaRef.current) {
        mediaRef.current.pause();
        try {
          mediaRef.current.currentTime = dur || 0;
        } catch {}
      }
      setShowSkip(false);
    } catch {}
  };

  if (!parsed?.media) {
    return (
      <pre style={{ whiteSpace: 'pre-wrap', fontSize: 13, background: '#f8f8f8', padding: 10, borderRadius: 3, maxHeight: 360, overflow: 'auto', ...style }}>
        {vastXml || 'No VAST provided'}
      </pre>
    );
  }

  const commonStyle: React.CSSProperties = kind === 'video'
    ? { width: '100%', maxWidth: 600, height: 340, background: '#000', margin: '0 auto', display: 'block' }
    : { width: '100%', maxWidth: 600, margin: '0 auto', display: 'block', background: '#f5f5f5' };

  return (
    <div style={{ position: 'relative', ...style }}>
      {kind === 'video' ? (
        <video ref={mediaRef as any} controls style={commonStyle}>
          <source src={parsed.media.url} type={parsed.media.type || 'video/mp4'} />
          Your browser does not support the video tag.
        </video>
      ) : (
        <audio ref={mediaRef as any} controls style={commonStyle}>
          <source src={parsed.media.url} />
          Your browser does not support the audio element.
        </audio>
      )}

      {/* ClickThrough overlay */}
      {kind === 'video' && parsed.meta.clickThrough && (
        <div
          ref={overlayRef}
          onClick={onClickOverlay}
          title="Open landing"
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            right: 0,
            bottom: kind === 'video' ? 40 : 0,
            cursor: 'pointer',
          }}
        />
      )}

      {/* Skip overlay */}
      {kind === 'video' && showSkip && (
        <button
          onClick={onSkip}
          style={{
            position: 'absolute',
            right: 10,
            top: 10,
            padding: '6px 10px',
            borderRadius: 4,
            border: 'none',
            background: 'rgba(0,0,0,0.7)',
            color: '#fff',
            cursor: 'pointer',
          }}
          title="Skip Ad"
        >
          Skip Ad
        </button>
      )}
    </div>
  );
}